import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import React, { useRef, useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  BadgeCheck, 
  Building2, 
  User, 
  ChevronDown, 
  ArrowRight, 
  Search, 
  Lock, 
  Clock,
  CheckCircle2,
  Phone,
  Globe,
  ExternalLink,
  LogOut,
  LayoutDashboard,
  HelpCircle,
  Calendar,
  Store,
  Users,
  ShieldCheck,
  TrendingUp,
  ChevronRight
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";


import logoAsset from "@/assets/logo.png.asset.json";
import { supabase } from "@/integrations/supabase/client";
import { getIcone } from "@/lib/icones";
import { useAuth } from "@/hooks/use-auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Card, CardContent } from "@/components/ui/card";


// Animated Number Component
const AnimatedNumber = React.memo(({ value, loading }: { value: number; loading: boolean }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef<HTMLSpanElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (loading || hasAnimated || !value) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setHasAnimated(true);
          
          if (observerRef.current) {
            observerRef.current.disconnect();
          }

          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setDisplayValue(value);
            return;
          }

          const duration = 1600;
          const startTime = performance.now();

          const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // easeOutExpo
            const easeOutExpo = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
            
            const currentCount = Math.floor(easeOutExpo * value);
            setDisplayValue(currentCount);

            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observerRef.current.observe(elementRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [value, loading, hasAnimated]);

  useEffect(() => {
    if (hasAnimated && displayValue !== value) {
      setDisplayValue(value);
    }
  }, [value, hasAnimated]);

  if (loading && !hasAnimated) {
    return <span className="text-primary animate-pulse opacity-50">...</span>;
  }

  return (
    <span ref={elementRef} className="text-primary">
      {displayValue.toLocaleString('pt-BR')}
    </span>
  );
});

AnimatedNumber.displayName = "AnimatedNumber";


export const Route = createFileRoute("/_main/")({
  component: Index,
});

function Index() {
  return <IndexContent />;
}

function IndexContent() {
  const { user, profile, authState, signOut } = useAuth();
  const loading = authState === 'carregando';
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const { data: segments = [] } = useQuery({
    queryKey: ['segmentos-home'],
    queryFn: async () => {
      const { data } = await supabase.from('segmentos').select('*').eq('ativo', true).order('ordem');
      return data || [];
    },
    staleTime: 1000 * 60 * 30,
  });

  const { data: contagemData = { total_fornecedores: 0, total_segmentos: 0, total_cidades: 0 } } = useQuery({
    queryKey: ['contagem-publica'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('contagem_publica');
      if (error) throw error;
      const result = (Array.isArray(data) ? data[0] : data) as any;
      return {
        total_fornecedores: Number(result?.total_fornecedores || 0),
        total_segmentos: Number(result?.total_segmentos || 0),
        total_cidades: Number(result?.total_cidades || 0)
      };
    },
    staleTime: 1000 * 60 * 15,
  });


  const isAprovado = !!user && profile?.status === 'aprovado';

  const { data: previewFornecedores = [], isLoading: isLoadingPreview } = useQuery({
    queryKey: ['preview-fornecedores', isAprovado],
    queryFn: async () => {
      if (isAprovado) {
        // Busca real para usuários aprovados (RLS garante acesso aos contatos)
        const { data } = await supabase
          .from('fornecedores')
          .select('id, nome, cidade, uf, polo, verificado, destaque, logo_url, telefone, site, instagram, email, segmentos(nome, slug, icone)')
          .eq('status', 'aprovado')
          .filter('suspeito', 'is', false)
          .order('destaque', { ascending: false })
          .order('verificado', { ascending: false })
          .limit(6);
        
        return (data || []).map(f => ({
          ...f,
          segmento_nome: (f.segmentos as any)?.nome,
          segmento_slug: (f.segmentos as any)?.slug,
          segmento_icone: (f.segmentos as any)?.icone
        }));
      } else {
        // Preview anônimo
        const { data } = await supabase.rpc('preview_fornecedores');
        return data || [];
      }
    },
    enabled: !loading, // Aguarda o carregamento do perfil/auth
    staleTime: 1000 * 60 * 15,
  });

  const { data: artigosHome } = useQuery({
    queryKey: ['artigos-home'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('artigos')
        .select('*, categorias_blog(nome, slug)')
        .eq('status', 'publicado')
        .order('publicado_em', { ascending: false })
        .limit(4)
      if (error) throw error
      return data
    },
    staleTime: 1000 * 60 * 15,
  });

  const contagem = {
    total_fornecedores: (contagemData as any).total_fornecedores || 0,
    total_segmentos: (contagemData as any).total_segmentos || 0,
    total_cidades: (contagemData as any).total_cidades || 0,
    loading: false,
    error: null
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    if (!user || profile?.status !== 'aprovado') {
      setLoginModalOpen(true);
    } else {
      navigate({ to: '/fornecedores', search: { q: searchQuery, page: 1 } });
    }
  };

  const handleSegmentClick = (slug: string) => {
    if (loading) return;

    if (!user) {
      setLoginModalOpen(true);
      return;
    }
    
    if (profile?.status !== 'aprovado') {
      toast.info("Aguarde a aprovação do seu perfil para acessar os segmentos.");
      navigate({ to: profile?.status === 'pendente' ? '/aguardando' : '/acesso-negado' });
    } else {
      navigate({ to: '/fornecedores', search: () => ({ segmentos: slug, page: 1 }) });
    }
  };

  return (
    <div>
      <Helmet>
        <title>Lista de Fornecedores e Atacadistas do Brasil | Gopage</title>
        <meta name="description" content={`Encontre mais de ${Math.floor(contagem.total_fornecedores / 10) * 10} fornecedores verificados. O melhor diretório de atacadistas do Brasil exclusivo para clientes Gopage.`} />
        <link rel="canonical" href="https://spark-smile-hub.lovable.app/" />
        <meta property="og:title" content="Lista de Fornecedores e Atacadistas do Brasil | Gopage" />
        <meta property="og:description" content={`Diretório com mais de ${Math.floor(contagem.total_fornecedores / 10) * 10} fornecedores e atacadistas verificados.`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      {/* SECTION 1 - Hero */}
      <section className="hero-bg text-white pt-24 pb-32 overflow-hidden min-h-[500px] flex items-center">

        <div className="container mx-auto px-4 text-center max-w-4xl relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-dark/40 backdrop-blur-sm border border-brand-lime/20 rounded-full text-brand-lime text-sm font-bold mb-8">
            <BadgeCheck className="w-4 h-4" />
            Exclusivo para clientes Gopage
          </div>
          <h1 style={{ color: '#FFFFFF' }} className="text-4xl md:text-6xl font-bold mb-6 tracking-tight leading-tight min-h-[1.2em]">
            {contagem.total_fornecedores > 0 ? (
              <>
                Mais de <span style={{ color: '#C6F764' }}><AnimatedNumber value={Math.floor(contagem.total_fornecedores / 10) * 10} loading={contagem.loading} /></span> fornecedores e atacadistas em todo o Brasil
              </>
            ) : (
              "Fornecedores e atacadistas em todo o Brasil"
            )}
          </h1>
          <p style={{ color: '#E5E7EB' }} className="text-xl md:text-2xl font-semibold mb-12 max-w-2xl mx-auto">
            Lista com os dados dos melhores fornecedores de todo o Brasil para a sua loja.
          </p>

          <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto mb-12 group">
            <div className="absolute inset-y-0 left-6 flex items-center pointer-events-none">
              <Search className="w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
            </div>
            <Input 
              type="text"
              placeholder="Qual fornecedor ou produto você procura?"
              className="w-full h-16 pl-14 pr-32 bg-white text-brand-dark rounded-full border-0 focus-visible:ring-2 focus-visible:ring-primary shadow-2xl"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button 
              type="submit"
              variant="onLight"
              className="absolute right-2 top-2 h-12 px-8 rounded-full bg-[#C6F764] text-[#0C1A12] font-bold"
            >
              Buscar
            </Button>
          </form>
        </div>
      </section>

      {/* SECTION 2 - Grade de Segmentos */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
            {segments.map((s: any) => {
              const Icon = getIcone(s.icone);
              return (
                <button 
                  key={s.id}
                  onClick={() => handleSegmentClick(s.slug)}
                  className="flex flex-col items-center p-6 border border-border rounded-3xl hover:border-primary hover:border-2 hover:-translate-y-1 hover:shadow-xl transition-all duration-200 ease-out group focus-visible:ring-2 focus-visible:ring-brand-dark outline-none"
                  aria-label={`Ver fornecedores de ${s.nome}`}
                >
                  <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mb-4 group-hover:scale-[1.04] group-hover:bg-[#B3E84F] transition-all duration-200 ease-out">
                    <Icon className="w-7 h-7 text-brand-dark" />
                  </div>
                  <span className="text-sm font-bold text-brand-dark text-center group-hover:font-semibold transition-all duration-200 ease-out">{s.nome}</span>
                </button>
              );
            })}
          </div>

          {/* Alguns dos fornecedores da lista */}
          {!isLoadingPreview && previewFornecedores && previewFornecedores.length > 0 && (
            <div className="mt-20">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-bold text-[#0C1A12] mb-2">{isAprovado ? "Alguns dos fornecedores disponíveis para você." : "Alguns dos fornecedores da lista"}</h2>
                <p className="text-[#6B7280]">
                  {isAprovado ? "" : "Uma pequena amostra do que você encontra no diretório completo."}
                </p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {previewFornecedores.map((f: any) => {
                  const Icon = getIcone(f.segmento_icone);
                  return (
                    <Card 
                      key={f.id} 
                      className="group cursor-pointer border-[#E5E7EB] rounded-[16px] hover:shadow-xl hover:border-primary/50 transition-all duration-300 relative overflow-hidden"
                      onClick={() => {
                        if (loading) return;
                        if (!isAprovado) {
                          setLoginModalOpen(true);
                        } else {
                          navigate({ to: `/fornecedores/${f.id}` as any });
                        }
                      }}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-8 h-8 bg-[#C6F764] rounded-lg flex items-center justify-center shrink-0">
                            <Icon className="w-[18px] h-[18px] text-[#0C1A12]" />
                          </div>
                          {f.verificado && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-[#C6F764] rounded-full text-[10px] font-bold text-[#0C1A12] uppercase tracking-wider">
                              <BadgeCheck className="w-3 h-3" />
                              Verificado
                            </div>
                          )}
                        </div>

                        <div className="mb-4">
                          <h3 className="text-[17px] font-semibold text-[#1A1A1A] line-clamp-1 group-hover:text-[#0C1A12] transition-colors">
                            {f.nome}
                          </h3>
                          <span className="text-sm text-[#6B7280]">
                            {f.segmento_nome}
                          </span>
                        </div>

                        {(f.cidade || f.uf) && (
                          <div className="flex items-center gap-2 text-[13px] text-[#6B7280] mb-5">
                            <Building2 className="w-3.5 h-3.5 shrink-0" />
                            <span>
                              {f.cidade}{f.cidade && f.uf ? ', ' : ''}{f.uf}
                            </span>
                          </div>
                        )}

                        {isAprovado ? (
                          <div className="flex gap-2">
                            {f.telefone && (
                              <a 
                                href={`https://wa.me/55${f.telefone.replace(/\D/g, '')}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-[10px] py-2.5 text-[13px] font-bold hover:bg-[#25D366]/90 transition-colors"
                              >
                                <Phone className="w-3.5 h-3.5" /> WhatsApp
                              </a>
                            )}
                            {f.site && (
                              <a 
                                href={f.site.startsWith('http') ? f.site : `https://${f.site}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="flex-1 flex items-center justify-center gap-2 bg-white border border-[#E5E7EB] text-[#0C1A12] rounded-[10px] py-2.5 text-[13px] font-bold hover:bg-gray-50 transition-colors"
                              >
                                <Globe className="w-3.5 h-3.5" /> Site
                              </a>
                            )}
                          </div>
                        ) : (
                          <div className="bg-[#F7F9F5] rounded-[10px] p-3 relative overflow-hidden group/blur">
                            <div className="space-y-2 blur-[5px] select-none pointer-events-none opacity-40">
                              <div className="h-2.5 bg-gray-300 rounded-full w-3/4"></div>
                              <div className="h-2.5 bg-gray-300 rounded-full w-1/2"></div>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Lock className="w-5 h-5 text-[#6B7280]" />
                            </div>
                            
                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-[#0C1A12]/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[1px]">
                              <span className="text-[12px] font-bold text-[#0C1A12]">Entre para ver o contato</span>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="flex justify-center">
                <Button 
                  onClick={() => {
                    if (loading) return;
                    if (isAprovado) {
                      navigate({ to: '/fornecedores', search: { page: 1 } });
                    } else if (!user) {
                      setLoginModalOpen(true);
                    } else {
                      navigate({ to: profile?.status === 'pendente' ? '/aguardando' : '/acesso-negado' });
                    }
                  }}
                  variant="onLight"
                  className="rounded-full px-10 h-[52px] font-semibold transition-all shadow-lg bg-[#0C1A12] text-white"
                >
                  Ver todos os fornecedores
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SECTION 3 - Como Funciona */}
      <section className="py-24 bg-[#F7F9F5]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0C1A12] mb-4">Como funciona</h2>
            <p className="text-[#6B7280] text-lg max-w-2xl mx-auto">Tudo o que você precisa para abastecer sua loja com segurança.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-[#E5E7EB]">
              <div className="w-12 h-12 bg-[#C6F764] rounded-2xl flex items-center justify-center mb-6">
                <Store className="w-6 h-6 text-[#0C1A12]" />
              </div>
              <h3 className="text-xl font-bold text-[#0C1A12] mb-4">Diretório Privado</h3>
              <p className="text-[#6B7280] leading-relaxed">Acesso exclusivo para clientes Gopage. Uma curadoria dos melhores fornecedores do Brás, Bom Retiro e outros polos.</p>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-[#E5E7EB]">
              <div className="w-12 h-12 bg-[#C6F764] rounded-2xl flex items-center justify-center mb-6">
                <ShieldCheck className="w-6 h-6 text-[#0C1A12]" />
              </div>
              <h3 className="text-xl font-bold text-[#0C1A12] mb-4">Verificação</h3>
              <p className="text-[#6B7280] leading-relaxed">Identificamos fornecedores com lojas físicas, CNPJ ativo e boa reputação no mercado atacadista.</p>
            </div>
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-[#E5E7EB]">
              <div className="w-12 h-12 bg-[#C6F764] rounded-2xl flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-[#0C1A12]" />
              </div>
              <h3 className="text-xl font-bold text-[#0C1A12] mb-4">Contato Direto</h3>
              <p className="text-[#6B7280] leading-relaxed">Fale direto com o fornecedor via WhatsApp sem intermediários. Negocie preços e prazos de forma transparente.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      {artigosHome && artigosHome.length > 0 && (
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#0C1A12] mb-4">
                Guias para lojistas
              </h2>
              <p className="text-[#6B7280] text-lg max-w-2xl mx-auto">
                Conteúdo prático sobre polos atacadistas, como escolher fornecedor e operar sua loja.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {artigosHome.map((artigo) => (
                <Link 
                  key={artigo.id} 
                  to="/guias/$categoria/$slug" 
                  params={{ 
                    categoria: artigo.categorias_blog?.slug || 'geral', 
                    slug: artigo.slug 
                  }}
                  className="group"
                >
                  <Card className="h-full border-none shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden bg-white rounded-2xl">
                    <div className="aspect-video relative overflow-hidden">
                      {artigo.imagem_capa ? (
                        <img 
                          src={artigo.imagem_capa} 
                          alt={artigo.titulo}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full bg-[#0C1A12] flex items-center justify-center">
                          <span className="text-white/20 font-bold text-xl">Gopage</span>
                        </div>
                      )}
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-[#C6F764] text-[#0C1A12] text-[10px] font-bold rounded-full uppercase tracking-wider">
                          {artigo.categorias_blog?.nome || 'Guia'}
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold text-[#1A1A1A] mb-2 group-hover:text-[#0C1A12] line-clamp-2 leading-tight">
                        {artigo.titulo}
                      </h3>
                      <p className="text-[#6B7280] text-sm mb-4 line-clamp-2">
                        {artigo.resumo}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-[#E5E7EB]">
                        <div className="flex items-center gap-3 text-[11px] text-[#6B7280]">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {artigo.tempo_leitura} min
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-[#C6F764] transition-transform group-hover:translate-x-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            <div className="text-center">
              <Link to="/guias">
                <Button variant="outlineLight" className="rounded-full px-8 h-[52px] font-semibold transition-all">
                  Ver todos os guias
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* SECTION - CTA */}
      <section className="py-24 bg-[#0C1A12] relative overflow-hidden">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Pronto para abastecer sua loja?</h2>
          <p className="text-[#E5E7EB] text-xl mb-12 max-w-2xl mx-auto">Acesse agora a lista e encontre os melhores fornecedores para o seu negócio.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/cadastro">
              <Button 
                size="lg" 
                variant="onDark"
                className="px-8 h-[52px] min-w-[200px] rounded-full shadow-xl transition-all"
              >
                Começar agora
              </Button>
            </Link>
            <Link to="/quero-ser-fornecedor">
              <Button 
                size="lg" 
                variant="outlineDark"
                className="px-8 h-[52px] min-w-[200px] rounded-full transition-all"
              >
                Quero Anunciar
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Login Modal */}
      <Dialog open={loginModalOpen} onOpenChange={setLoginModalOpen}>
        <DialogContent className="sm:max-w-md rounded-3xl border-none shadow-2xl p-0 overflow-hidden">
          <div className="bg-[#0C1A12] p-8 text-center text-white">
            <div className="w-16 h-16 bg-[#C6F764] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Lock className="w-8 h-8 text-[#0C1A12]" />
            </div>
            <DialogTitle className="text-2xl font-bold mb-2">Acesso exclusivo para clientes Gopage</DialogTitle>
            <DialogDescription className="text-[#E5E7EB]">
              Os contatos dos fornecedores são liberados apenas para quem tem uma conta Gopage ativa. Confirmamos seu acesso pelo e-mail cadastrado.
            </DialogDescription>
          </div>
          <div className="p-8 bg-white flex flex-col gap-3">
            <Link to="/entrar" onClick={() => setLoginModalOpen(false)}>
              <Button variant="onLight" className="w-full h-12 font-bold rounded-xl shadow-none">
                Já sou cliente Gopage — entrar
              </Button>
            </Link>
            <Link to="/cadastro" onClick={() => setLoginModalOpen(false)}>
              <Button variant="outlineLight" className="w-full h-[52px] font-semibold rounded-full transition-all">
                Sou cliente e ainda não criei meu acesso
              </Button>
            </Link>
            <div className="text-center mt-2">
              <a 
                href="https://gopage.bio?utm_source=listafornecedores&utm_medium=modal-acesso&utm_campaign=trial" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[13px] text-[#6B7280] hover:text-[#0C1A12] underline underline-offset-4 decoration-[#D1D5DB]"
              >
                Ainda não sou cliente Gopage. Quero conhecer.
              </a>
            </div>
          </div>

        </DialogContent>
      </Dialog>
    </div>
  );
}